import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import './ThreeBlocksAdmin.css';

const ThreeBlocksAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleBlockUpdate = (block, field, value) => {
    updateContent(`${contentPath}.${block}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleNestedUpdate = (pathSuffix, value) => {
    updateContent(`${contentPath}.${pathSuffix}`, value);
    setSectionData(getContent(contentPath));
  };

  const renderIconTitleDescList = (pathSuffix, items, itemLabel, template) => (
    <div className="form-group full-width">
      <div className="features-header">
        <label>{itemLabel}</label>
        <button
          type="button"
          className="btn-add-feature"
          onClick={() => handleNestedUpdate(pathSuffix, [...(items || []), template])}
        >
          <i className="fas fa-plus"></i> Ajouter
        </button>
      </div>
      {(items || []).map((item, index) => (
        <div key={index} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
          <div className="form-grid">
            <div className="form-group">
              <label>Icône</label>
              <input type="text" value={item.icon || ''} onChange={(e) => handleNestedUpdate(`${pathSuffix}.${index}.icon`, e.target.value)} placeholder="fas fa-star" />
            </div>
            <div className="form-group">
              <label>Titre</label>
              <input type="text" value={item.title || ''} onChange={(e) => handleNestedUpdate(`${pathSuffix}.${index}.title`, e.target.value)} />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea value={item.description || ''} onChange={(e) => handleNestedUpdate(`${pathSuffix}.${index}.description`, e.target.value)} rows={2} />
            </div>
          </div>
          <button type="button" className="btn-delete" onClick={() => handleNestedUpdate(pathSuffix, items.filter((_, i) => i !== index))}>
            <i className="fas fa-trash"></i> Supprimer
          </button>
        </div>
      ))}
    </div>
  );

  if (!sectionData) {
    return (
      <div className="three-blocks-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="three-blocks-admin">
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-book-medical"></i>
            Histoire, Vision & Mission
          </h2>
          <p>Gérez les 3 blocs principaux de la page À propos (image + texte simple)</p>
        </div>
      </div>

      {/* Notre Histoire */}
      <div className="block-section">
        <div className="block-section-header">
          <h3><i className="fas fa-history"></i> Notre Histoire</h3>
        </div>
        
        <div className="form-grid">
          <div className="form-group">
            <label>Icône (optionnel)</label>
            <input
              type="text"
              value={sectionData.histoire?.icon || ''}
              onChange={(e) => handleBlockUpdate('histoire', 'icon', e.target.value)}
              placeholder="fas fa-history"
            />
          </div>

          <div className="form-group full-width">
            <label>Image</label>
            <ImageUpload
              value={sectionData.histoire?.image}
              onChange={(url) => handleBlockUpdate('histoire', 'image', url)}
              label="Télécharger une image"
            />
          </div>

          <div className="form-group full-width">
            <label>Titre</label>
            <input
              type="text"
              value={sectionData.histoire?.title || ''}
              onChange={(e) => handleBlockUpdate('histoire', 'title', e.target.value)}
              placeholder="Notre Histoire"
            />
          </div>

          <div className="form-group full-width">
            <label>Sous-titre</label>
            <input
              type="text"
              value={sectionData.histoire?.subtitle || ''}
              onChange={(e) => handleBlockUpdate('histoire', 'subtitle', e.target.value)}
              placeholder="L'aventure DentoXcellence..."
            />
          </div>

          <div className="form-group full-width">
            <label>Description</label>
            <textarea
              value={sectionData.histoire?.intro || ''}
              onChange={(e) => handleBlockUpdate('histoire', 'intro', e.target.value)}
              placeholder="DentoXcellence est né d'une vision partagée..."
              rows={6}
            />
          </div>

          <div className="form-group full-width">
            <div className="features-header">
              <label>Points clés (chronologie)</label>
              <button
                type="button"
                className="btn-add-feature"
                onClick={() => handleNestedUpdate('histoire.highlights', [...(sectionData.histoire?.highlights || []), { year: '', title: '', description: '' }])}
              >
                <i className="fas fa-plus"></i> Ajouter
              </button>
            </div>
            {(sectionData.histoire?.highlights || []).map((item, index) => (
              <div key={index} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Année</label>
                    <input type="text" value={item.year || ''} onChange={(e) => handleNestedUpdate(`histoire.highlights.${index}.year`, e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Titre</label>
                    <input type="text" value={item.title || ''} onChange={(e) => handleNestedUpdate(`histoire.highlights.${index}.title`, e.target.value)} />
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea value={item.description || ''} onChange={(e) => handleNestedUpdate(`histoire.highlights.${index}.description`, e.target.value)} rows={2} />
                  </div>
                </div>
                <button type="button" className="btn-delete" onClick={() => handleNestedUpdate('histoire.highlights', sectionData.histoire.highlights.filter((_, i) => i !== index))}>
                  <i className="fas fa-trash"></i> Supprimer
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notre Vision */}
      <div className="block-section">
        <div className="block-section-header">
          <h3><i className="fas fa-eye"></i> Notre Vision</h3>
        </div>
        
        <div className="form-grid">
          <div className="form-group">
            <label>Icône (optionnel)</label>
            <input
              type="text"
              value={sectionData.vision?.icon || ''}
              onChange={(e) => handleBlockUpdate('vision', 'icon', e.target.value)}
              placeholder="fas fa-eye"
            />
          </div>

          <div className="form-group full-width">
            <label>Image</label>
            <ImageUpload
              value={sectionData.vision?.image}
              onChange={(url) => handleBlockUpdate('vision', 'image', url)}
              label="Télécharger une image"
            />
          </div>

          <div className="form-group full-width">
            <label>Titre</label>
            <input
              type="text"
              value={sectionData.vision?.title || ''}
              onChange={(e) => handleBlockUpdate('vision', 'title', e.target.value)}
              placeholder="Notre Vision"
            />
          </div>

          <div className="form-group full-width">
            <label>Sous-titre</label>
            <input
              type="text"
              value={sectionData.vision?.subtitle || ''}
              onChange={(e) => handleBlockUpdate('vision', 'subtitle', e.target.value)}
              placeholder="Vers l'excellence dentaire de demain"
            />
          </div>

          <div className="form-group full-width">
            <label>Introduction (optionnelle, si pas de bloc ambition)</label>
            <textarea
              value={sectionData.vision?.intro || ''}
              onChange={(e) => handleNestedUpdate('vision.intro', e.target.value)}
              placeholder="Texte d'introduction affiché si le bloc ambition est vide..."
              rows={3}
            />
          </div>

          <div className="form-group full-width">
            <label>Titre ambition</label>
            <input
              type="text"
              value={sectionData.vision?.ambition?.title || ''}
              onChange={(e) => handleNestedUpdate('vision.ambition.title', e.target.value)}
              placeholder="Notre Ambition"
            />
          </div>

          <div className="form-group full-width">
            <label>Description ambition</label>
            <textarea
              value={sectionData.vision?.ambition?.description || ''}
              onChange={(e) => handleNestedUpdate('vision.ambition.description', e.target.value)}
              placeholder="Être le référent incontournable..."
              rows={6}
            />
          </div>

          <div className="form-group full-width">
            <label>Titre objectifs stratégiques</label>
            <input
              type="text"
              value={sectionData.vision?.strategicGoals?.title || ''}
              onChange={(e) => handleNestedUpdate('vision.strategicGoals.title', e.target.value)}
              placeholder="Nos Objectifs Stratégiques"
            />
          </div>

          {renderIconTitleDescList(
            'vision.strategicGoals.goals',
            sectionData.vision?.strategicGoals?.goals,
            'Objectifs stratégiques',
            { icon: '', title: '', description: '' }
          )}
        </div>
      </div>

      {/* Notre Mission */}
      <div className="block-section">
        <div className="block-section-header">
          <h3><i className="fas fa-bullseye"></i> Notre Mission</h3>
        </div>
        
        <div className="form-grid">
          <div className="form-group">
            <label>Icône (optionnel)</label>
            <input
              type="text"
              value={sectionData.mission?.icon || ''}
              onChange={(e) => handleBlockUpdate('mission', 'icon', e.target.value)}
              placeholder="fas fa-bullseye"
            />
          </div>

          <div className="form-group full-width">
            <label>Image</label>
            <ImageUpload
              value={sectionData.mission?.image}
              onChange={(url) => handleBlockUpdate('mission', 'image', url)}
              label="Télécharger une image"
            />
          </div>

          <div className="form-group full-width">
            <label>Titre</label>
            <input
              type="text"
              value={sectionData.mission?.title || ''}
              onChange={(e) => handleBlockUpdate('mission', 'title', e.target.value)}
              placeholder="Notre Mission"
            />
          </div>

          <div className="form-group full-width">
            <label>Sous-titre</label>
            <input
              type="text"
              value={sectionData.mission?.subtitle || ''}
              onChange={(e) => handleBlockUpdate('mission', 'subtitle', e.target.value)}
              placeholder="Former les dentistes de demain"
            />
          </div>

          <div className="form-group full-width">
            <label>Introduction</label>
            <textarea
              value={sectionData.mission?.intro || sectionData.mission?.description || ''}
              onChange={(e) => handleNestedUpdate('mission.intro', e.target.value)}
              placeholder="Notre mission est de..."
              rows={4}
            />
          </div>

          <div className="form-group full-width">
            <label>Description détaillée</label>
            <textarea
              value={sectionData.mission?.description || ''}
              onChange={(e) => handleBlockUpdate('mission', 'description', e.target.value)}
              placeholder="Notre mission est de..."
              rows={6}
            />
          </div>

          <div className="form-group full-width">
            <label>Titre piliers</label>
            <input
              type="text"
              value={sectionData.mission?.pillars?.title || ''}
              onChange={(e) => handleNestedUpdate('mission.pillars.title', e.target.value)}
              placeholder="Nos Piliers d'Action"
            />
          </div>

          {renderIconTitleDescList(
            'mission.pillars.items',
            sectionData.mission?.pillars?.items,
            'Piliers',
            { icon: '', title: '', description: '' }
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreeBlocksAdmin;

